import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  setEmail,
  setToken,
  setProfile,
  setUserProfile,
  setContactId,
} from '@/store/reducers/authSlice';
import { loginRequest } from './app/modules/auth/authConfig';

import { useLazyFetchProfileQuery } from './app/modules/profile/profile.services';
import { Sidebar } from './app/shared/components/Layout/Sidebar/Sidebar';
import { Topbar } from './app/shared/components/Layout/Topbar/Topbar';
import { Notistack } from './app/shared/components/Notistack';
import { PAGE_TYPE } from './app/shared/constants/segmentConstants';
import { useAnalytics } from './app/shared/hooks/useAnalytics';
import { useContactUserMappingMutation, useContactUserMappingWithEmailMutation, useCreateUserEntityMutation } from './app.services';
import { useAppDispatch, useAppSelector } from './store/hooks';
import type { RootState } from './store';

const menuItems = [
  { text: 'Home', link: '/home' },
  { text: 'Upload Documents', link: '/upload' },
  { text: 'View Documents', link: '/documents' },
  { text: 'Products', link: '/products' },
  // { text: 'Profile', link: '/profile' },
  // { text: 'Settings', link: '/settings' },
];

function App() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { globalError } = useAppSelector((state) => state.ui);
  const { identifyUser } = useAnalytics({page_type: PAGE_TYPE.ILENDING_HUB_UPLOAD});
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useAppDispatch();
  const authToken = useAppSelector((state) => state.auth.token);
  const [fetchProfile] = useLazyFetchProfileQuery();
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [openViaButton, setOpenViaButton] = useState(!isMobile);

  const { contactId, metadataProcessed, userProfile: reduxUserProfile } = useSelector((state: RootState) => state.auth);

  const sidebarOpen = openViaButton;

  const [createUserEntity] = useCreateUserEntityMutation();
  const [contactUserMapping] = useContactUserMappingMutation();
  const [contactUserMappingWithEmail] = useContactUserMappingWithEmailMutation();

  useEffect(() => {
    const acquireToken = async () => {
      if (!isAuthenticated || accounts.length === 0) return;
  
      const account = accounts[0];
  
      dispatch(
        setProfile({
          ...account,
          tenantProfiles: Object.fromEntries(
            account.tenantProfiles ?? []
          ),
        })
      );
  
      dispatch(setEmail(account.username || ''));
  
      try {
        const response = await instance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account,
        });
  
        dispatch(setToken(response.accessToken));
      } catch (error) {
        console.error('Token acquisition failed:', error);
        Notistack.generalError('Token acquisition failed');
      } finally {
        setIsAuthInitialized(true);
      }
    };
  
    acquireToken();
  }, [isAuthenticated, accounts, instance, dispatch]);

  useEffect(() => {
    const createUserAndMapContactId = async () => {
      const isUserAlreadyCreated =
        sessionStorage.getItem('isUserCreated');

      if (
        !isAuthenticated ||
        !authToken || !metadataProcessed
      ) {
        return;
      }

      try {
        let profileToUse = reduxUserProfile;
      
        // Fetch profile only if not in redux
        if (!profileToUse?.emailId) {
          const userProfile = await fetchProfile().unwrap();
      
          profileToUse = {
            firstName: userProfile?.firstName || '',
            lastName: userProfile?.lastName || '',
            emailId: userProfile?.email || '',
          };
      
          dispatch(setUserProfile(profileToUse));
        }
      
        // Create user only once
        if (!isUserAlreadyCreated && profileToUse?.emailId) {
          await createUserEntity(profileToUse).unwrap();
        }
      
        if (!contactId && profileToUse?.emailId) {
          const userDetails = await contactUserMappingWithEmail({
            email: profileToUse.emailId,
          }).unwrap();
        
          if (userDetails?.contactId) {
            identifyUser(userDetails.contactId);
            dispatch(setContactId(userDetails.contactId));
          }
        
          return;
        }
        
        if (contactId) {
          await contactUserMapping({
            contactId,
            emailId: profileToUse?.emailId || '',
          }).unwrap();
        }
        sessionStorage.setItem('isUserCreated', 'true');
      } catch (error) {
        console.error(
          'create user and user entity contact id mapping failed',
          error
        );   
        Notistack.generalError('Create user and user entity - contact id mapping failed');  
        sessionStorage.removeItem('isUserCreated');
      }
    };

    createUserAndMapContactId();
  }, [
    authToken,
    isAuthenticated,
    metadataProcessed,
    fetchProfile,
    createUserEntity,
    contactUserMapping,
    contactUserMappingWithEmail,
    dispatch,
    navigate,
  ]);

  useEffect(() => {
    if (globalError) {
      navigate(`/error${location.search}`);
    }
  }, [globalError, navigate]);

  if (isAuthenticated && !isAuthInitialized) {
    return null;
  }

  return (
    <Box sx={{
      display: 'flex', height: '100vh', width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      <Sidebar
        open={sidebarOpen}
        onClose={() => setOpenViaButton(false)}
        fetchedMenuItems={menuItems}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          transition: 'all 0.3s ease',
        }}
      >
        <Topbar
          onMenuClick={() => setOpenViaButton((prev) => !prev)}
        />

        <Box
          sx={{
            flexGrow: 1,
            p: '10px',
            pt: '10px',
            backgroundColor: '#F3F4F6',
            overflow: 'auto',
            '@media (min-width: 600px)': {
              p: '28px',
              pt: '28px',
            },
            '@media (min-width: 900px)': {
              p: '32px',
              pt: '32px',
            },
          }}
        >

          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

export default App;